import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FaCopy, FaCheck, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';

const MpesaRegisterModal = ({ isOpen, onClose, confirmUrl, validateUrl, environment }) => {
  const { token } = useAuth();

  const [status, setStatus] = useState('idle'); // 'idle' | 'registering' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(null);
  const timerRef = useRef(null);
  const copyTimerRef = useRef(null);

  // Reset to idle every time the modal opens so stale error/success messages don't linger
  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setMessage('');
      setCopiedUrl(null);
    }
  }, [isOpen]);

  // Clean up auto-close timer on unmount to avoid state update on unmounted component
  useEffect(() => () => {
    clearTimeout(timerRef.current);
    clearTimeout(copyTimerRef.current);
  }, []);

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(url);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopiedUrl(null), 2000);
    }).catch(() => {});
  };

  const handleRegister = async () => {
    setStatus('registering');
    setMessage('');
    const authToken = token || localStorage.getItem('authToken');
    const headers = { Authorization: `Bearer ${authToken}` };
    try {
      const res = await axios.post(
        `${rootUrl}/api/mpesa-settings/register-urls`,
        {},
        { headers }
      );
      const msg = res.data?.alreadyRegistered
        ? 'These URLs are already active on Safaricom — your shortcode is ready to receive payments.'
        : 'Callback URLs registered with Safaricom. Payments to this Paybill will now be routed here.';
      setMessage(msg);
      setStatus('success');
      timerRef.current = setTimeout(() => onClose(), 3000);
    } catch (err) {
      const msg = err.response?.data?.message || 'Registration failed. Please try again.';
      setMessage(msg);
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  const envLabel = environment === 'production' ? 'Production' : 'Sandbox';
  const envClasses = environment === 'production'
    ? 'bg-green-100 text-green-800'
    : 'bg-amber-100 text-amber-800';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={status !== 'registering' ? onClose : undefined}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="daraja-modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 id="daraja-modal-title" className="text-lg font-semibold text-gray-800">
            Register with Daraja
          </h2>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${envClasses}`}>
            {envLabel}
          </span>
        </div>

        {/* Overwrite notice */}
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-4">
          If a different set of URLs is already registered for this shortcode, this will replace them.
        </p>

        {/* URL display rows */}
        {[
          { label: 'Confirmation URL', url: confirmUrl },
          { label: 'Validation URL', url: validateUrl },
        ].map(({ label, url }) => (
          <div key={label} className="mb-3">
            <div className="text-xs font-medium text-gray-600 mb-1">{label}</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-gray-100 rounded px-2 py-1.5 break-all font-mono">
                {url}
              </code>
              <button
                type="button"
                onClick={() => copyToClipboard(url)}
                className="shrink-0 text-gray-500 hover:text-orange-500 transition-colors"
                aria-label={`Copy ${label}`}
                title={`Copy ${label}`}
              >
                {copiedUrl === url ? <FaCheck className="text-green-500" /> : <FaCopy />}
              </button>
            </div>
          </div>
        ))}

        {/* Status messages */}
        {status === 'success' && (
          <div className="flex items-center gap-2 text-green-700 text-sm mt-4">
            <FaCheckCircle className="shrink-0" /> {message}
          </div>
        )}
        {status === 'error' && (
          <div className="text-red-600 text-sm mt-4">{message}</div>
        )}

        {/* Action buttons — hidden after success (modal auto-closes) */}
        {status !== 'success' && (
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={status === 'registering'}
              className="px-4 py-2 text-sm rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRegister}
              disabled={status === 'registering'}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors disabled:opacity-60"
            >
              {status === 'registering' && <FaSpinner className="animate-spin" />}
              Register with Daraja
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MpesaRegisterModal;
